import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { eventService } from '@/services/eventService';
import { EventProvider, EventTask } from '@/types';
import { ProviderMultiSelect } from './ProviderMultiSelect';

interface NewTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  /** Event-providers (`evento_proveedores`) already assigned to this event.
   *  Passed in by the caller to avoid an extra round-trip. */
  eventProviders: EventProvider[];
  /** Fired after the task is successfully created so the caller can refresh. */
  onCreated?: (task: EventTask) => void;
}

export const NewTaskModal: React.FC<NewTaskModalProps> = ({
  open,
  onOpenChange,
  eventId,
  eventProviders,
  onCreated,
}) => {
  const { toast } = useToast();
  const [titulo, setTitulo] = useState('');
  const [fechaInicio, setFechaInicio] = useState<Date | undefined>(undefined);
  const [fechaFin, setFechaFin] = useState<Date | undefined>(undefined);
  const [notas, setNotas] = useState('');
  const [providerIds, setProviderIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitulo('');
    setFechaInicio(undefined);
    setFechaFin(undefined);
    setNotas('');
    setProviderIds([]);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const validate = (): string | null => {
    if (!titulo.trim()) return 'El título es obligatorio';
    if (!fechaInicio) return 'La fecha de inicio es obligatoria';
    if (fechaFin && fechaFin < fechaInicio) return 'La fecha fin no puede ser anterior al inicio';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      toast({ title: 'Faltan datos', description: err, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const created = await eventService.createEventTask({
        event_id: eventId,
        titulo: titulo.trim(),
        inicio: fechaInicio!,
        fin: fechaFin ?? fechaInicio!,
        notas: notas.trim() || undefined,
      });
      if (providerIds.length > 0) {
        await eventService.setTaskProviders(created.id, providerIds);
      }
      toast({ title: 'Tarea creada', description: `"${created.titulo}" se guardó correctamente.` });
      onCreated?.(created);
      reset();
      onOpenChange(false);
    } catch (e) {
      console.error('Error creating task:', e);
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : 'No se pudo crear la tarea',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nueva Tarea</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="titulo">Título de la tarea</Label>
            <Input
              id="titulo"
              placeholder="Ej: Reunión con proveedor..."
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="inicio">Fecha y hora inicio</Label>
              <DateTimePicker date={fechaInicio} setDate={setFechaInicio} />
            </div>
            <div>
              <Label htmlFor="fin">Fecha y hora fin (opcional)</Label>
              <DateTimePicker date={fechaFin} setDate={setFechaFin} />
            </div>
          </div>
          <div>
            <Label htmlFor="notas">Notas (opcional)</Label>
            <Textarea
              id="notas"
              placeholder="Detalles adicionales sobre la tarea..."
              rows={3}
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
            />
          </div>
          <div>
            <Label>Proveedores relacionados (opcional)</Label>
            {eventProviders.length === 0 ? (
              <p className="text-xs text-gray-500 mt-1">
                Aún no hay proveedores asignados a este evento.
              </p>
            ) : (
              <ProviderMultiSelect
                options={eventProviders}
                value={providerIds}
                onChange={setProviderIds}
              />
            )}
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleSubmit}
              disabled={submitting || !titulo || !fechaInicio}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crear Tarea
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
